import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Mail, Lock, Eye, EyeOff, Phone } from 'lucide-react';
import Header from './components/Header6';
import Footer from './components/Footer6';

const Signup6 = () => {
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [formData, setFormData] = useState({ fullName: '', email: '', phone: '', password: '', confirmPassword: '' });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (formData.password !== formData.confirmPassword) return;
        navigate('/template6');
    };

    const passwordStrength = useMemo(() => {
        const pwd = formData.password;
        if (!pwd) return { level: 0, label: '', color: '' };
        let score = 0;
        if (pwd.length >= 8) score++;
        if (pwd.length >= 12) score++;
        if (/[A-Z]/.test(pwd)) score++;
        if (/[a-z]/.test(pwd)) score++;
        if (/[0-9]/.test(pwd)) score++;
        if (/[^A-Za-z0-9]/.test(pwd)) score++;
        if (score <= 2) return { level: 1, label: 'Weak', color: '#C0392B' };
        if (score <= 4) return { level: 2, label: 'Fair', color: '#D4A017' };
        return { level: 3, label: 'Strong', color: '#5D7A3A' };
    }, [formData.password]);

    const passwordsMatch = formData.confirmPassword ? formData.password === formData.confirmPassword : true;
    const inputStyle = "w-full px-4 py-3.5 bg-[#FAF7F2] border border-[#D4C5B0] text-[#2C1810] text-sm rounded-sm outline-none transition-colors duration-300 focus:border-[#8B4513] placeholder-[#7C6B5D]";

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Noto+Serif+Display:wght@400;600;700&family=Nunito:wght@300;400;600&display=swap');
            `}</style>
            <div style={{ fontFamily: "'Nunito', -apple-system, sans-serif" }} className="min-h-screen bg-[#F5F0E8]">
                <Header />

                <main className="pt-20 min-h-screen flex">
                    <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
                        <img src="https://images.pexels.com/photos/3224610/pexels-photo-3224610.jpeg?auto=compress&cs=tinysrgb&w=1200" alt="Handloom artisans" className="absolute inset-0 w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-r from-[#3B1A08]/30 via-[#3B1A08]/50 to-[#F5F0E8]" />
                        <div className="relative z-10 flex flex-col justify-center items-start p-16 max-w-lg">
                            <div className="w-12 h-px bg-[#8B4513] mb-6" />
                            <h2 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-4xl xl:text-5xl font-normal text-[#F5F0E8] leading-tight mb-4">
                                Join the Dharti Family
                            </h2>
                            <p className="text-[#D4C5B0] text-base leading-relaxed" style={{ fontWeight: 300 }}>
                                Create an account to discover authentic handloom textiles and support artisan communities across India.
                            </p>
                        </div>
                    </div>

                    <div className="flex-1 flex items-center justify-center px-6 py-12 lg:py-0">
                        <div className="w-full max-w-md">
                            <div className="text-center mb-10">
                                <h1 onClick={() => navigate('/template6')} style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-3xl font-bold text-[#8B4513] cursor-pointer tracking-[3px] mb-3">DHARTI</h1>
                                <h2 style={{ fontFamily: "'Noto Serif Display', serif" }} className="text-2xl lg:text-3xl font-normal text-[#2C1810] tracking-wide">Create Account</h2>
                                <p className="text-[#7C6B5D] text-sm mt-2" style={{ fontWeight: 300 }}>Join us and explore India's finest handloom traditions</p>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div>
                                    <label className="block text-xs font-semibold text-[#2C1810] mb-2 uppercase tracking-[1px]">Full Name</label>
                                    <div className="relative">
                                        <User size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7C6B5D]" />
                                        <input type="text" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="Your full name" required className={inputStyle} style={{ paddingLeft: '44px', fontFamily: "'Nunito', sans-serif" }} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[#2C1810] mb-2 uppercase tracking-[1px]">Email</label>
                                    <div className="relative">
                                        <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7C6B5D]" />
                                        <input type="email" name="email" value={formData.email} onChange={handleInputChange} placeholder="your@email.com" required className={inputStyle} style={{ paddingLeft: '44px', fontFamily: "'Nunito', sans-serif" }} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[#2C1810] mb-2 uppercase tracking-[1px]">Phone</label>
                                    <div className="relative">
                                        <Phone size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7C6B5D]" />
                                        <input type="tel" name="phone" value={formData.phone} onChange={handleInputChange} placeholder="+91 98765 43210" required className={inputStyle} style={{ paddingLeft: '44px', fontFamily: "'Nunito', sans-serif" }} />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[#2C1810] mb-2 uppercase tracking-[1px]">Password</label>
                                    <div className="relative">
                                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7C6B5D]" />
                                        <input type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleInputChange} placeholder="Create a password" required className={inputStyle} style={{ paddingLeft: '44px', paddingRight: '44px', fontFamily: "'Nunito', sans-serif" }} />
                                        <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7C6B5D]" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {formData.password && (
                                        <div className="mt-2">
                                            <div className="flex gap-1 mb-1">
                                                {[1, 2, 3].map(l => (<div key={l} style={{ flex: 1, height: '3px', borderRadius: '2px', backgroundColor: l <= passwordStrength.level ? passwordStrength.color : '#D4C5B0' }} />))}
                                            </div>
                                            <p style={{ fontSize: '11px', color: passwordStrength.color, fontWeight: 600 }}>{passwordStrength.label}</p>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-xs font-semibold text-[#2C1810] mb-2 uppercase tracking-[1px]">Confirm Password</label>
                                    <div className="relative">
                                        <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7C6B5D]" />
                                        <input type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleInputChange} placeholder="Confirm your password" required className={inputStyle} style={{ paddingLeft: '44px', paddingRight: '44px', fontFamily: "'Nunito', sans-serif", borderColor: !passwordsMatch ? '#C0392B' : undefined }} />
                                        <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#7C6B5D]" style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                        </button>
                                    </div>
                                    {!passwordsMatch && <p style={{ fontSize: '11px', color: '#C0392B', marginTop: '4px', fontWeight: 600 }}>Passwords do not match</p>}
                                </div>

                                <button type="submit" className="w-full" style={{ padding: '16px', backgroundColor: '#8B4513', color: '#FFFFFF', border: 'none', borderRadius: '2px', fontSize: '13px', fontWeight: 600, letterSpacing: '1.5px', textTransform: 'uppercase', cursor: 'pointer', fontFamily: "'Nunito', sans-serif", transition: 'background-color 0.3s', marginTop: '8px' }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#6B3410')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#8B4513')}>
                                    Create Account
                                </button>
                            </form>

                            <p className="text-center mt-8 text-sm text-[#7C6B5D]" style={{ fontWeight: 300 }}>
                                Already have an account?{' '}
                                <span onClick={() => navigate('/template6/login')} className="text-[#8B4513] font-semibold cursor-pointer hover:text-[#6B3410]">Sign In</span>
                            </p>
                        </div>
                    </div>
                </main>

                <Footer />
            </div>
        </>
    );
};

export default Signup6;
